import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import './DataGrid.css';

const DataGrid = ({
                      data = [],
                      columns = [],
                      loading = false,
                      error = '',
                      currentPage = 1,
                      pageSize = 20,
                      totalRecords = 0,
                      onPageChange,
                      onPageSizeChange,
                      sortField = '',
                      sortOrder = 'asc',
                      onSort,
                      filters = {},
                      filterTypes = {},
                      onFilterChange,
                      onRowDoubleClick,
                      renderActions,
                      renderCard,
                      emptyIcon: EmptyIcon,
                      emptyMessage = 'Kayıt bulunamadı',
                      // Boş liste bir hatadır ya da bir kuraldır — hangisi olduğunu söylemeyen
                      // ekran kullanıcıyı destek hattına yollar. İpucu, kaydın NEDEN burada
                      // olmadığını ve ne yapılması gerektiğini yazar.
                      emptyHint,
                      loadingMessage = 'Yükleniyor...',
                  }) => {
    const [showFilters, setShowFilters] = useState({});
    const [localFilters, setLocalFilters] = useState(filters);
    const debounceRef = useRef({});

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const getFilterLabel = (type) => {
        const labels = {
            contains: 'İçerir',
            notcontains: 'İçermez',
            eq: 'Eşittir',
            neq: 'Eşit Değil',
            startsWith: 'Başlar',
            endsWith: 'Biter',
        };
        return labels[type] || type;
    };

    const handleSort = (field) => {
        if (!onSort) return;
        if (sortField === field) {
            onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            onSort(field, 'asc');
        }
    };

    const handleFilterInput = (field, value) => {
        setLocalFilters(prev => ({ ...prev, [field]: value }));

        if (debounceRef.current[field]) {
            clearTimeout(debounceRef.current[field]);
        }

        debounceRef.current[field] = setTimeout(() => {
            onFilterChange &&
            onFilterChange(field, value, filterTypes[field] || 'contains');
        }, 600);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    const skeletonWidths = useMemo(() => {
        const widths = [75, 60, 85, 70, 90, 65, 80, 55, 72, 88];
        return [...Array(10)].map((_, rowIdx) =>
            columns.reduce((acc, col, colIdx) => {
                acc[col.field] = widths[(rowIdx + colIdx) % widths.length];
                return acc;
            }, {})
        );
    }, [columns]);

    const columnWidths = useMemo(() => {
        const charWidth = 8;
        const padding = 20;
        const maxWidth = 300;
        const minWidth = 60;

        return columns.reduce((acc, col) => {
            let maxLen = col.header ? col.header.length : 0;

            data.forEach(row => {
                let value = col.render ? null : row[col.field];
                if (value !== null && value !== undefined) {
                    const strValue = String(value);
                    if (strValue.length > maxLen) {
                        maxLen = strValue.length;
                    }
                }
            });

            const calculatedWidth = Math.min(maxWidth, Math.max(minWidth, maxLen * charWidth + padding));
            acc[col.field] = calculatedWidth;
            return acc;
        }, {});
    }, [columns, data]);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages && onPageChange) {
            onPageChange(page);
        }
    };

    const getPageNumbers = () => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage <= 3) {
            return [1, 2, 3, 4, 5];
        }

        if (currentPage >= totalPages - 2) {
            return [
                totalPages - 4,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
                totalPages,
            ];
        }

        return [
            currentPage - 2,
            currentPage - 1,
            currentPage,
            currentPage + 1,
            currentPage + 2,
        ];
    };

    // Liste boşken tablo iskeleti bilgi taşımaz ama sütun genişlikleri kadar yer kaplar:
    // başlıklar sayfayı yatay taşırıyor, boş durum metni de tablo genişliğine göre
    // ortalandığı için kaydırınca ekrandan kayıyordu. Kayıt yoksa yalnız boş durum çizilir.
    // Aktif filtre varsa başlıklar KALIR — kullanıcı aramasını düzeltebilmeli.
    const hasActiveFilter = Object.values(filters || {}).some(v => v !== '' && v != null);
    const showEmptyOnly = !loading && data.length === 0 && !hasActiveFilter;

    if (showEmptyOnly) {
        return (
            <div className="data-grid-wrapper">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="data-grid-container">
                    <div className="empty-cell">
                        {EmptyIcon && <EmptyIcon size={48} />}
                        <p>{emptyMessage}</p>
                        {emptyHint && <div className="empty-hint">{emptyHint}</div>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="data-grid-wrapper">
            {error && <div className="alert alert-error">{error}</div>}

            <div className="data-grid-container">
                <table className="data-grid">
                    <thead>
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.field}
                                className={`${col.sortable ? 'sortable' : ''} ${col.className || ''}`}
                                style={{ width: col.width || columnWidths[col.field], maxWidth: 300 }}
                                onClick={() => col.sortable && handleSort(col.field)}
                            >
                                <div className="th-content">
                                    <span>{col.header}</span>
                                    {col.sortable && sortField === col.field && (
                                        <span className="sort-indicator">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                                    )}
                                </div>
                            </th>
                        ))}
                        {renderActions && <th className="actions-column">İşlemler</th>}
                    </tr>

                    <tr className="filter-row">
                        {columns.map(col => (
                            <th key={`filter-${col.field}`} style={{ width: col.width || columnWidths[col.field], maxWidth: 300 }}>
                                {col.filterable && (
                                    <div className="filter-cell-inline">
                                        <input
                                            type="text"
                                            placeholder="Ara"
                                            value={localFilters[col.field] || ''}
                                            onChange={(e) => handleFilterInput(col.field, e.target.value)}
                                            className="filter-input-inline"
                                        />
                                        <div className="filter-icon-wrapper">
                                            <button
                                                className={`filter-icon-btn ${localFilters[col.field] ? 'active' : ''}`}
                                                onClick={() =>
                                                    setShowFilters(p => ({ ...p, [col.field]: !p[col.field] }))
                                                }
                                                title={getFilterLabel(filterTypes[col.field] || 'contains')}
                                            >
                                                <Filter size={14} />
                                            </button>

                                            {showFilters[col.field] && (
                                                <div className="filter-dropdown-menu">
                                                    {['contains', 'notcontains', 'eq', 'neq', 'startsWith', 'endsWith'].map(type => (
                                                        <button
                                                            key={type}
                                                            className={`filter-option ${(filterTypes[col.field] || 'contains') === type ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                onFilterChange &&
                                                                onFilterChange(
                                                                    col.field,
                                                                    localFilters[col.field] || '',
                                                                    type
                                                                );
                                                                setShowFilters({});
                                                            }}
                                                        >
                                                            {getFilterLabel(type)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </th>
                        ))}
                        {renderActions && <th />}
                    </tr>
                    </thead>

                    <tbody>
                    {loading ? (
                        [...Array(Math.min(pageSize, 10))].map((_, idx) => (
                            <tr key={`skeleton-${idx}`} className="skeleton-row">
                                {columns.map(col => (
                                    <td key={`skeleton-${idx}-${col.field}`}>
                                        <div className="skeleton-cell">
                                            <div className="skeleton-content" style={{ width: `${skeletonWidths[idx]?.[col.field] || 70}%` }} />
                                        </div>
                                    </td>
                                ))}
                                {renderActions && (
                                    <td className="actions-cell">
                                        <div className="skeleton-cell skeleton-actions">
                                            <div className="skeleton-btn" />
                                            <div className="skeleton-btn" />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    ) : data.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length + (renderActions ? 1 : 0)}>
                                <div className="empty-cell">
                                    {EmptyIcon && <EmptyIcon size={48} />}
                                    <p>{emptyMessage}</p>
                                    {emptyHint && <div className="empty-hint">{emptyHint}</div>}
                                </div>
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => (
                            <tr
                                key={row.id || index}
                                onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
                                style={{ cursor: onRowDoubleClick ? 'pointer' : 'default' }}
                            >
                                {columns.map(col => (
                                    <td key={col.field} className={col.className || ''} style={{ maxWidth: 300 }}>
                                        {col.render ? col.render(row) : row[col.field]}
                                    </td>
                                ))}
                                {renderActions && <td className="actions-cell">{renderActions(row)}</td>}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            <div className="data-cards">
                {loading ? (
                    [...Array(Math.min(pageSize, 6))].map((_, idx) => (
                        <div key={`skeleton-card-${idx}`} className="skeleton-card">
                            <div className="skeleton-card-header">
                                <div className="skeleton-content" style={{ width: '60%', height: '20px' }} />
                                <div className="skeleton-content" style={{ width: '80px', height: '24px', borderRadius: '12px' }} />
                            </div>
                            <div className="skeleton-card-body">
                                <div className="skeleton-content" style={{ width: '90%' }} />
                                <div className="skeleton-content" style={{ width: '75%' }} />
                                <div className="skeleton-content" style={{ width: '60%' }} />
                            </div>
                            <div className="skeleton-card-footer">
                                <div className="skeleton-btn" />
                                <div className="skeleton-btn" />
                            </div>
                        </div>
                    ))
                ) : data.length === 0 ? (
                    <div className="empty-cell">
                        {EmptyIcon && <EmptyIcon size={48} />}
                        <p>{emptyMessage}</p>
                        {emptyHint && <div className="empty-hint">{emptyHint}</div>}
                    </div>
                ) : (
                    renderCard && data.map((row, index) => (
                        <div key={row.id || index} onClick={() => onRowDoubleClick && onRowDoubleClick(row)}>
                            {renderCard(row)}
                        </div>
                    ))
                )}
            </div>

            {totalRecords > 0 && (
                <div className="pagination">
                    <div className="pagination-info">
                        {startRecord}-{endRecord} / {totalRecords}
                    </div>

                    <div className="pagination-controls">
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
                            className="page-size-select"
                        >
                            {[10, 20, 50, 100].map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>

                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                        >
                            <ChevronLeft size={18} />
                        </button>

                        <div className="page-numbers">
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                    onClick={() => goToPage(pageNum)}
                                    disabled={loading}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>

                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataGrid;
